// M5.1 — static NPC templates (vendors); M5.2 — quest-givers.
//
// NPCs are placed via tilemap `npcSpawns` (one (x, y, facing, templateId) per
// entry), broadcast once per zone via the `npcsInZone` private message on
// enterWorld / teleport — they don't ride the 20Hz worldSnapshot because they
// never move.
//
// Capability is optional: an NpcTemplate may carry a `vendor` block, a
// `quests` block, or both. Click dispatch decides which modal to open
// based on which capability is present (client-side, since the npcsInZone
// snapshot already exposes templateId).
//
// M7.2 made the runtime catalogue swappable via setNpcCatalogue, mirroring
// the items / mobs pattern.

import { z } from "zod";
import type { NpcQuestData } from "./quests.js";

export interface VendorData {
  // Item template IDs offered by this vendor. Infinite stock — the server
  // doesn't track inventory counts. Mutable for wire compat with the admin
  // publish path; runtime mutation should still go through setNpcCatalogue.
  stockTemplateIds: string[];
  // Player sell-back rate. 0.25 = 25% of `template.value`. Affixes do NOT
  // modify the sell-back price for M5.1.
  sellMultiplier: number;
}

export interface NpcTemplate {
  id: string;
  name: string;
  // Greeting text shown above the vendor / dialog panel.
  dialogText: string;
  // Farewell line surfaced as a toast after a successful vendorTransact and
  // when the player walks out of range. Optional — falls back to a generic
  // "Pleasure doing business." on the client when missing.
  farewellText?: string;
  // Optional capability — vendor opens a buy/sell modal on click.
  vendor?: VendorData;
  // Optional capability — quest-giver opens an interact dialog on click.
  quests?: NpcQuestData;
}

// Curated low/mid-tier general stock for the Eldoria vendor. Excludes top-tier
// items (plate_mail, steel_warhammer, etc.) so players still have a reason to
// hunt drops past the early game.
const ELDORIA_GENERAL_STOCK: string[] = [
  "rusty_dagger",
  "iron_sword",
  "iron_mace",
  "short_bow",
  "apprentice_staff",
  "wooden_shield",
  "leather_cap",
  "leather_tunic",
  "leather_pants",
  "leather_boots",
  "leather_belt",
  "shoulder_pads",
  "traveler_cloak",
  "copper_bracer",
  "minor_health_potion",
  "minor_mana_potion",
  "bread",
  // Playtest sandbox: 50 kg / 1 c. Lets a tester stress the weight gate
  // without grinding copper. See items.ts:anvil.
  "anvil",
];

const t = <T extends Record<string, NpcTemplate>>(o: T): T => o;

// Quest-giver quest list for Captain Roric — three starter kill quests that
// point players at the existing Verdoie / Darkwood mob loop. Ordered easiest
// first so the dialog UI reads as a difficulty ramp.
const ELDORIA_CAPTAIN_QUESTS: string[] = [
  "eldoria_cave_rat_hunt",
  "eldoria_wolf_cull",
  "eldoria_goblin_scout",
];

export const BUILTIN_NPC_TEMPLATES = t({
  eldoria_general_vendor: {
    id: "eldoria_general_vendor",
    name: "Merna the Outfitter",
    dialogText:
      "Welcome to Eldoria. Pots, blades, and a stout cloak — whatever an adventurer needs.",
    // {name} is substituted with the player's character name on the client.
    farewellText: "Safe travels, {name}. Come back when your purse jingles again.",
    vendor: {
      stockTemplateIds: ELDORIA_GENERAL_STOCK,
      sellMultiplier: 0.25,
    },
  },
  eldoria_captain: {
    id: "eldoria_captain",
    name: "Captain Roric",
    dialogText:
      "Hail, traveler. The garrison's spread thin — I could use a hand. Take a job from the board.",
    farewellText: "Walk light, {name}. The roads have teeth.",
    quests: {
      questIds: ELDORIA_CAPTAIN_QUESTS,
    },
  },
});

export const NPC_TEMPLATES = BUILTIN_NPC_TEMPLATES;
export type NpcTemplateId = keyof typeof BUILTIN_NPC_TEMPLATES;

// =============================================================================
// Runtime registry (M7.2)
// =============================================================================

const runtimeNpcRegistry = new Map<string, NpcTemplate>(
  Object.entries(BUILTIN_NPC_TEMPLATES),
);

export function getNpcTemplate(id: string): NpcTemplate | undefined {
  return runtimeNpcRegistry.get(id);
}

export function getNpcCatalogue(): NpcTemplate[] {
  return Array.from(runtimeNpcRegistry.values());
}

export function setNpcCatalogue(npcs: readonly NpcTemplate[]): void {
  runtimeNpcRegistry.clear();
  for (const n of npcs) runtimeNpcRegistry.set(n.id, n);
}

export function _resetNpcCatalogueForTests(): void {
  runtimeNpcRegistry.clear();
  for (const [id, tpl] of Object.entries(BUILTIN_NPC_TEMPLATES)) {
    runtimeNpcRegistry.set(id, tpl);
  }
}

// =============================================================================
// Zod schemas (M7.2)
// =============================================================================

const NPC_ID_REGEX = /^[a-z][a-z0-9_]{2,60}$/;

const VendorDataSchema = z
  .object({
    stockTemplateIds: z.array(z.string().min(1)).max(200),
    sellMultiplier: z.number().min(0).max(1),
  })
  .strict();

const NpcQuestDataSchema = z
  .object({
    questIds: z.array(z.string().min(1)).max(50),
  })
  .strict();

export const NpcTemplateSchema = z
  .object({
    id: z.string().regex(NPC_ID_REGEX),
    name: z.string().min(1).max(80),
    dialogText: z.string().min(1).max(500),
    farewellText: z.string().min(1).max(200).optional(),
    vendor: VendorDataSchema.optional(),
    quests: NpcQuestDataSchema.optional(),
  })
  .strict();

export const NpcCatalogueSchema = z.array(NpcTemplateSchema);
